import {get, isObject} from 'lodash-es';

import {ILookupValue} from 'shared/types/lookups';

import {ICustomSelectEntry} from './custom-select';

/**
 * Создает функцию-форматер для преобразования значений полей перед отображением в селекте
 *
 * Эта функция возвращает форматтер, который извлекает значения из объектов или массивов
 * по указанному пути (path). Полезно для работы с сложными структурами данных, когда
 * нужно отображать не весь объект, а конкретное свойство.
 *
 * @param {string} [path] - Путь к свойству в объекте (в формате lodash.get).
 * Если не указан, возвращается исходное значение.
 *
 * @returns {Function} Функция-форматер, которая принимает значение поля и возвращает отформатированное значение
 *
 * @remarks
 * - Для объектов: извлекает значение по пути с помощью lodash.get
 * - Для массивов: применяет извлечение к каждому элементу массива
 * - Для примитивов: возвращает исходное значение
 * - Если значение по пути не найдено, возвращает undefined
 */
export const createFieldFormatterForSelect = (path?: string) => (
    fieldValue: any,
) => {
    if (isObject(fieldValue) && path) return get(fieldValue, path) ?? undefined;
    if (Array.isArray(fieldValue) && path) return fieldValue.map(item => get(item, path)) ?? undefined;
    return fieldValue;
};

/**
 * Дефолтные настройки для кастом селекта для работы с valuesList и др. лукапами.
 * @param {string} url - url для получения данных лукапа.
 */
export const getCustomSelectDefaultPreset = (url: string) => ({
    url,
    valuePath: 'lookupCode',
    labelPath: 'meaning',
    isClearable: true,
    showSearch: true,
});

export type TSortDirection = 'asc' | 'desc';

/**
 * Ограниченный тип ключа, который гарантирует существование ключа в типе значения V
 *
 * @template V - Тип значения, который должен содержать указанный ключ
 */
export type TLookupValueKey<V> = keyof V;

/**
 * Тип функции-компаратора для сортировки элементов ICustomSelectEntry
 *
 * @template L - Тип метки
 * @template V - Тип значения
 *
 * @param {ICustomSelectEntry<L, V>} optionA - Первая опция для сравнения
 * @param {ICustomSelectEntry<L, V>} optionB - Вторая опция для сравнения
 * @returns {number} Отрицательное число если optionA < optionB, положительное если optionA > optionB, ноль если равны
 */
export type TSortComparator<L, V> = (optionA: ICustomSelectEntry<L, V>, optionB: ICustomSelectEntry<L, V>) => number

/**
 * Создает настраиваемую функцию-компаратор для сортировки опций кастомного селекта
 *
 * Эта функция генерирует компаратор, который можно использовать для сортировки элементов ICustomSelectEntry
 * по указанному свойству объекта значения и направлению сортировки.
 *
 * @template L - Тип метки в ICustomSelectEntry (по умолчанию string)
 * @template V - Тип значения в ICustomSelectEntry (по умолчанию ILookupValue)
 *
 * @param {TSortDirection} [direction='asc'] - Направление сортировки: 'asc' по возрастанию, 'desc' по убыванию
 * @param {TLookupValueKey<V>} sortBy - Ключ свойства объекта значения, по которому выполняется сортировка
 *
 * @returns {TSortComparator<L, V>} Функция-компаратор для использования с Array.prototype.sort
 *
 * @remarks
 * Компаратор безопасно обрабатывает null/undefined значения, преобразуя их в пустые строки.
 * Все строковые сравнения выполняются без учета регистра (приводятся к нижнему регистру).
 */
export const createSortComparator = <L = string, V = ILookupValue>(
    direction: TSortDirection = 'asc',
    sortBy: TLookupValueKey<V>,
): (TSortComparator<L, V>) => (
  optionA,
  optionB
) => {
        const getComparableValue = (option: ICustomSelectEntry<L, V>): string => {
            const value = option?.value?.[sortBy];
            return String(value ?? '').toLowerCase();
        };

        const valueA = getComparableValue(optionA);
        const valueB = getComparableValue(optionB);

        const comparisonResult = valueA.localeCompare(valueB);
        return direction === 'asc' ? comparisonResult : -comparisonResult;
    };
